import type { CSSProperties, HTMLAttributes } from 'react';
import { sparkAssetUrl } from '../../utils/sparkAssetUrl';
import './SentimentRating.css';

export type SentimentValue = 'negative' | 'neutral' | 'positive';

export interface SentimentRatingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Currently selected sentiment; `null` when nothing is selected. */
  value: SentimentValue | null;
  /**
   * Called with the clicked sentiment. Clicking the already-selected face
   * clears the rating (`null`) so the field stays optional.
   */
  onChange?: (value: SentimentValue | null) => void;
  disabled?: boolean;
  /** Accessible group label. Defaults to "How is your experience?". */
  'aria-label'?: string;
}

const FACES: ReadonlyArray<{ value: SentimentValue; label: string; asset: string }> = [
  { value: 'negative', label: 'Negative', asset: 'illustrations/illustration-feedback-face-sad.png' },
  { value: 'neutral', label: 'Neutral', asset: 'illustrations/illustration-feedback-face-neutral.png' },
  { value: 'positive', label: 'Positive', asset: 'illustrations/illustration-feedback-face-love.svg' },
];

/**
 * Three hand-drawn sentiment faces (Figma "Share your thoughts" 9261:8298):
 * sad, neutral and heart-eyes. Faces are rendered through a CSS mask so the
 * colour comes from the stylesheet — grey when unselected, brand blue when
 * selected. Nothing is preselected and the rating is optional.
 */
export function SentimentRating({
  value,
  onChange,
  disabled = false,
  'aria-label': ariaLabel = 'How is your experience?',
  className = '',
  ...rest
}: SentimentRatingProps) {
  const classes = ['spark-sentiment-rating', disabled ? 'spark-sentiment-rating-disabled' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div role="group" aria-label={ariaLabel} className={classes} {...rest}>
      {FACES.map((face) => {
        const selected = value === face.value;
        const faceStyle = {
          '--spark-sentiment-rating-face-image': `url(${sparkAssetUrl(face.asset)})`,
        } as CSSProperties;
        return (
          <button
            key={face.value}
            type="button"
            className={[
              'spark-sentiment-rating-option',
              `spark-sentiment-rating-option-${face.value}`,
              selected ? 'spark-sentiment-rating-option-selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={face.label}
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange?.(selected ? null : face.value)}
          >
            <span className="spark-sentiment-rating-face" style={faceStyle} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
